/**
 * Node Modules
 */
import React, { createElement } from 'react'
import { Helmet } from 'react-helmet'
import marksy from 'marksy/jsx'
import { graphql } from 'gatsby'

/**
 * Application Modules
 */
import PageContainer from '../../components/page-container'
import Metadata from '../../components/metadata'
import Footer from '../../components/footer'
import Header from '../../components/header'

import './style.css'

const compile = marksy({
  createElement,
  elements: {},
})

const cookiesPolicy = `
# Information Collected Using Cookies and other Web
Technologies

This Cookie Policy (the "Cookie Policy") is intended to inform you of how Kinzoo
Technologies Inc. (collectively, “Kinzoo,” “we,” “our” or “us”) uses cookie and
related technologies (pixel, tags or beacons) (together “cookies” or “cookie”), on
[https://kinzoo.com/](https://kinzoo.com/) website (the "Website").

Please take a minute to read and understand this Cookie Policy. This Cookie Policy
should also be read in conjunction with our [Privacy Policy](http://kinzoo.com/privacy-policy). 

## Cookies

Like most websites, we use various technologies to collect information, and this
may include sending cookies to user computers or mobile devices.
Cookies are small text files that are placed on your device by a web server when
you access our web site. It enables the website to remember your actions and
preferences (such as login, language, font size and other display preferences) over
a period of time, for example so you don’t have to re-enter them whenever you
come back to the website or browse from one page to another. Cookies do not
cause any damage to the terminal, but make it easier, for example, to find
preferences, pre-fill certain fields and adapt the content of the services on the site
visited. Such data allows the improvement of ergonomics and services according
to your interests.

A cookie can be classified by its lifespan and the domain to which it belongs. By
lifespan, a cookie is either a:

- **Session Cookies**: These are erased when the user closes the browser.
- **Persistent Cookies**: These remain on the user’s computer/device for a pre-
defined period of time.

As for the domain to which it belongs, there are either:

- **First-party Cookies**: These are set by the web server of the visited page and
share the same domain (in this case [kinzoo.com](http://kinzoo.com/)) or;
- **Third-party Cookies**: These are stored by a different domain to the visited
page’s domain. This can happen when the webpage references a file, such
as JavaScript, located outside its domain.

Cookies used

- **Necessary Cookies**: These cookies are strictly necessary for our Website to
function properly and ensure our services are accessible to you (e.g., log-in
functionality, load balancing, navigation, filling in forms). The Website
cannot function properly without these cookies.
- **Preference Cookies**: Preference cookies enable our Website to provide
enhanced features or settings based on your previous visits and selections,
such as language preferences, remembering log-in details
- **Statistic Cookies**: Statistic cookies enable us to understand how visitors
interact with our Website by collecting and reporting information
anonymously.
- **Marketing Cookies**: Marketing cookies are used to track visitors across our
Website. The intention is to display ads that are relevant and engaging for
the individual user and thereby more valuable for publishers and third-
party advertisers.

## Pixel Tags/Web Beacons

A pixel tag (also known as a web beacon) is a piece of code embedded in the
services that collects information about visitors’ engagement on that web page.
The use of a pixel allows us to record, for example, that a visitor has visited a
particular web page or clicked on a particular advertisement.

## Analytics

We may allow others to provide analytics services on our behalf in connection
with Website (but not the App). These entities may use cookies, web beacons,
device identifiers and other technologies to collect information about your and
your child’s use of the Kinzoo website and other websites, including IP address,
web browser, mobile network information, pages viewed, time spent on pages or 
in apps, links clicked, and conversion information. This information may be used
by Kinzoo and others to, among other things, analyze and track data, determine
the popularity of certain content, make recommendations, deliver targeted
content, and better understand your online activity.
We may also use Google Analytics and Google Analytics Demographics and
Interest Reporting to collect information regarding visitor behavior and visitor
demographics on some of our services, and to develop website content. This
analytics data is not tied to any Personal Information. For more information about
Google Analytics, please visit [www.google.com/policies/privacy/partners/](https://www.google.com/policies/privacy/partners/). You
can opt out of Google’s collection and Processing of data generated by your use of
the Website by going to [tools.google.com/dlpage/gaoptout](http://tools.google.com/dlpage/gaoptout).

## How we use cookies

When you visit or access the Website, we may – by means of cookies, beacons,
tags, scripts, and/or other similar technologies – automatically collect technical
information about the devices and software you use to access the Website, such
as the type of Internet browser or mobile device you use, the website or source
that linked or referred you to the service, your IP address or device ID (or other
persistent identifier that uniquely identifies your computer or mobile device on
the Internet), the operating system of your computer or mobile device, and other
similar technical information. These same technologies (or a combination of
them) may also be used to help us:

- Compile analytics and statistics about your use of and interaction with the
Website.
- Gather important testing, and performance data about our service.
- Provide customer support through contact forms and chat.
- Gather demographic information about our user base.
- Perform other similar functions.

We will not use cookies or similar technologies other than for the purposes stated
in this Policy.

## Third party advertising and ad networks

Kinzoo partners with third-party advertisers, ad server companies, and ad
networks (“third-party advertising companies”) to promote our service. If you
respond to one of our advertisements and visit or register to use our Website, we
or our service providers may provide these third-party advertising companies with
identifiers from your device or computer, such as an IP address or device ID, to
help us analyze our user acquisition efforts.

## Your choices

Most web and mobile device browsers automatically accept cookies, but if you
prefer, you can change your browser to prevent that or to notify you each time a
cookie is set. For a full list of cookies used on the Website, see below. Some
browser manufacturers provide comprehensive help relating to cookie
management in their products. Please see the respective browser websites for
more information or check the "Help" menu of your browser or operating system
to learn how to change your tracking settings or cookie preferences. Instructions
to this effect are provided each browser’s website:

- **For Internet Explore**r: [https://support.microsoft.com/en-us/help/17442/windows-internet-explorer-delete-manage-cookies](https://support.microsoft.com/en-us/help/17442/windows-internet-explorer-delete-manage-cookies)
- **For Firefox**: [https://support.mozilla.org/en-US/kb/enable-and-disable-
cookies-website-preferences](https://support.mozilla.org/en-US/kb/enable-and-disable-)
- **For Safari**: [https://support.apple.com/en-gb/HT201265](https://support.apple.com/en-gb/HT201265)
- **For Chrome**: [https://support.google.com/chrome/answer/95647?hl=en-
GB&hlrm=en](https://support.google.com/chrome/answer/95647?hl=en-)

For any other browser not mentioned above, the user should not hesitate to
consult the cookie management help information available on the Website. You
can also visit the below link for an overview of how to block or delete cookies on
the most common browsers: [http://www.allaboutcookies.org](http://www.allaboutcookies.org/).

Please note, however, that by blocking or deleting cookies used on the Website,
you may not be able to use the full functionality of the Website.

## Questions?

Kinzoo Technologies, Inc. is a company located outside of the European Union. In order
to comply with Art 27 EU GDPR, [GDPR-Rep.eu](http://gdpr-rep.eu/) has been nominated as our

representative in the European Union. If you want to make use of your data privacy
rights, please visit: Our public Privacy dashboard or or email us at [privacy@kinzoo.com](mailto:privacy@kinzoo.com).

## Detail listing of cookies used on the Website
`

function CookiesPolicy({data}) {
  const {
    referenceCookiesPolicy,
  } = data.contentfulSingletonStaticPageMetadata

  return (
    <PageContainer>
      <Helmet>
        <meta charSet="utf-8" />
        <link rel="canonical" href="https://kinzoo.com/cookies-policy/" />
        <meta name="og:url" content="https://kinzoo.com/cookies-policy/" />=
      </Helmet>
      <Metadata {...referenceCookiesPolicy} />
      <Header />
      

      <article className="generic-page">
        <div className="generic-page__head">
          <div className="grid generic-page__grid">
            <h1 className="generic-page__hed column-start-1 column-end-6 column-start-2--t column-end-11--t column-start-3--d column-end-10--d">
              Kinzoo Cookies Policy
            </h1>
            <p className="generic-page__dek column-start-1 column-end-6 column-start-2--t column-end-11--t column-start-3--d column-end-10--d">
              Last Revised: August 26, 2020
            </p>
          </div>
        </div>
        <div className="grid">
          <div className="blog-article__article column-start-1 column-start-2--t column-start-3--d column-end-6 column-end-11--t column-end-10--d">
            {compile(cookiesPolicy).tree}

            <div style={{overflow: 'scroll', width: '100%'}}>
            <table>
              <thead>
                <tr>
                  <th>COOKIE</th>
                  <th>PROVIDER</th>
                  <th>DURATION</th>
                  <th>DESCRIPTION</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th colSpan="4">Necessary cookies</th>
                </tr>
                <tr>
                  <td>__cf_bm</td>
                  <td>kinzoo.zendesk.com</td>
                  <td>1 day</td>
                  <td rowSpan="6">
                    Enabling basic functions such as page navigation and access to secure areas of the website. The website cannot function properly without these cookies.
                  </td>
                </tr>
                <tr>
                  <td>__cfruid</td>
                  <td>kinzoo.zendesk.com</td>
                  <td>Session</td>
                </tr>
                <tr>
                  <td>__zlcstore</td>
                  <td>kinzoo.com</td>
                  <td>Persistent</td>
                </tr>
                <tr>
                  <td>AWSALB</td>
                  <td>zopim.com</td>
                  <td>6 days</td>
                </tr>
                <tr>
                  <td>AWSALBCORS</td>
                  <td>zopim.com</td>
                  <td>6 days</td>
                </tr>
                <tr>
                  <td>ZD-suid</td>
                  <td>kinzoo.com</td>
                  <td>Persistent</td>
                </tr>

                <tr>
                  <th colSpan="4">Preference cookies</th>
                </tr>
                <tr>
                  <td>{'@@scroll#'}</td>
                  <td>kinzoo.com</td>
                  <td>Session</td>
                  <td rowSpan="2">
                    Preference cookies allow a website to retain information that changes the way the site behaves or displays, such as your preferred language or the region in which you are located.
                  </td>
                </tr>
                <tr>
                  <td>ZD-store</td>
                  <td>kinzoo.com</td>
                  <td>Persistent</td>
                </tr>

                <tr>
                  <th colSpan="4">Statistic cookies</th>
                </tr>
                <tr>
                  <td>_ga</td>
                  <td>kinzoo.com</td>
                  <td>2 years</td>
                  <td rowSpan="6">
                    Statistic cookies helps to monitor use and performance of the website using randomly generated client identifiers.
                  </td>
                </tr>
                <tr>
                  <td>_gat</td>
                  <td>kinzoo.com</td>
                  <td>1 day</td>
                </tr>
                <tr>
                  <td>_gid</td>
                  <td>kinzoo.com</td>
                  <td>1 day</td>
                </tr>
                <tr>
                  <td>collect</td>
                  <td>google-analytics.com</td>
                  <td>Session</td>
                </tr>
                <tr>
                  <td>ZD-buid</td>
                  <td>kinzoo.com</td>
                  <td>Persistent</td>
                </tr>
                <tr>
                  <td>ZD-currentTime</td>
                  <td>kinzoo.com</td>
                  <td>Session</td>
                </tr>

                <tr>
                  <th colSpan="4">Marketing cookies </th>
                </tr>
                <tr>
                  <td>__zlcmid</td>
                  <td>kinzoo.com</td>
                  <td>1 year</td>
                  <td rowSpan="5">
                    Marketing cookies are used to track visitors across our Website. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third party advertisers.
                  </td>
                </tr>
                <tr>
                  <td>_fbp</td>
                  <td>kinzoo.com</td>
                  <td>3 months</td>
                </tr>
                <tr>
                  <td>fr</td>
                  <td>facebook.com</td>
                  <td>3 months</td>
                </tr>
                <tr>
                  <td>r/collect </td>
                  <td>doubleclick.net</td>
                  <td>Session</td>
                </tr>
                <tr>
                  <td>tr</td>
                  <td>facebook.com</td>
                  <td>Session</td>
                </tr>
              </tbody>
            </table>
            </div>

            <br />
            <br />
            <br />
            <br />
          </div>
        </div>
      </article>
      <Footer />
    </PageContainer>
  )
}

export default CookiesPolicy

export const pageQuery = graphql`
  query CookiesPageQuery {
    contentfulSingletonStaticPageMetadata(contentful_id: {eq: "37wd06meITbnwAFYKdxp70"}) {
      referenceCookiesPolicy {
        description
        keywords
        ogDescription
        ogTitle
        ogType
        pageTitle
        twitterCard
        twitterCreator
        twitterDescription
        twitterTitle
        ogImage {
          file {
            url
          }
        }
        twitterImage {
          file {
            url
          }
        }
      }
    }
  }
`
